import React, { Fragment } from 'react';
import { createPortal } from 'react-dom';
import { Transition } from '@headlessui/react';
import { XCircle } from '@phosphor-icons/react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  variant?: 'dialog' | 'fullscreen' | 'confirm';
  className?: string;
  // Confirm dialog props
  confirmProps?: {
    message?: string;
    onConfirm: () => void;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmStyle?: 'primary' | 'danger';
    isLoading?: boolean;
  };
}

export function Modal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  variant = 'dialog',
  className = '',
  confirmProps
}: ModalProps) {
  if (!isOpen) return null;

  const renderConfirmContent = () => {
    if (!confirmProps) return null;

    const {
      message,
      onConfirm,
      confirmLabel = 'Confirm',
      cancelLabel = 'Cancel',
      confirmStyle = 'primary',
      isLoading = false
    } = confirmProps;

    const confirmButtonClass = `inline-flex justify-center rounded-lg px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
      confirmStyle === 'danger'
        ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
        : 'bg-[#361E06] hover:bg-[#4A2B0B] focus:ring-[#361E06]'
    } disabled:opacity-50 disabled:cursor-not-allowed`;

    const cancelButtonClass = 
      'inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#361E06] disabled:opacity-50 disabled:cursor-not-allowed';

    return (
      <>
        <p className="text-sm text-gray-500">{message}</p>
        <div className="mt-6 flex flex-row-reverse gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={confirmButtonClass}
          >
            {isLoading ? 'Processing...' : confirmLabel}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className={cancelButtonClass}
          >
            {cancelLabel}
          </button>
        </div>
      </>
    );
  };

  const content = (
    <Transition
      show={isOpen}
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      {variant === 'fullscreen' ? (
        // Fullscreen variant
        <div className="fixed inset-0 bg-[#FAF6F1] z-[9999] overflow-y-auto">
          <div className="min-h-screen p-4 flex flex-col">
            {/* Header */}
            {title && (
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-medium text-[#361E06]">{title}</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-[rgba(54,30,6,0.075)]"
                >
                  <XCircle className="w-8 h-8 text-[#361E06]" weight="fill"/>
                </button>
              </div>
            )}
            
            {/* Body */}
            <div className={`flex-1 ${className}`}>
              {children}
            </div>
          </div>
        </div>
      ) : (
        // Dialog & Confirm variants
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={onClose}
          />
          
          {/* Content */}
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div 
                className={`relative bg-white rounded-2xl shadow-xl transform transition-all max-w-lg w-full ${className}`}
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                {title && (
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-medium text-[#361E06]">{title}</h2>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-full hover:bg-[rgba(54,30,6,0.075)]"
                    >
                      <XCircle className="w-6 h-6 text-[#361E06]" weight="fill"/>
                    </button>
                  </div>
                )}
                
                {/* Body */}
                <div className="p-6">
                  {variant === 'confirm' ? renderConfirmContent() : children}
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      )}
    </Transition>
  );

  return createPortal(content, document.body);
} 